/**
 * Workspace registries — the single source of truth for what an admin is
 * allowed to put inside a workspace.
 *
 * Mirrors the pattern in `src/theme/copy/registry.ts`: a typed allowlist with
 * metadata used both for validation (Zod schema rejects unknown ids) and for
 * the editor UI (so the admin sees friendly labels + tags).
 *
 * Adding a new nav destination or dashboard widget here is a deliberate act —
 * if it isn't in the registry, it can't be exposed via a workspace.
 */

/** Permission resource ids align with the RBAC service. */
export type PermissionResource =
  | "devices"
  | "applications"
  | "configurations"
  | "tags"
  | "users"
  | "roles"
  | "settings"
  | "tenants"
  | "vulnerabilities"
  | "compliance"
  | "scripts"
  | "logs"
  | "patching"
  | "enrollment"
  | "analytics"
  | "dashboard";

export interface NavItemEntry {
  id: string;
  /** Canonical label shown in the editor. */
  title: string;
  /** Route the link navigates to. */
  url: string;
  /** Section header it falls under by default. */
  section: "Overview" | "Platform" | "System Administration" | "Tenant Administration";
  /** Permission resource required to use the destination. Workspaces never widen this. */
  permission?: PermissionResource;
  /** Free-text tags used by the editor's "filter by audience" facet. */
  tags: ReadonlyArray<string>;
}

export const NAV_ITEM_REGISTRY = {
  "nav.gettingStarted": {
    id: "nav.gettingStarted",
    title: "Getting Started",
    url: "/getting-started",
    section: "Overview",
    tags: ["onboarding"],
  },
  "nav.dashboard": {
    id: "nav.dashboard",
    title: "Dashboard",
    url: "/analytics",
    section: "Overview",
    permission: "analytics",
    tags: ["fleet", "overview"],
  },
  "nav.devices": {
    id: "nav.devices",
    title: "Devices",
    url: "/devices",
    section: "Platform",
    permission: "devices",
    tags: ["fleet"],
  },
  "nav.applications": {
    id: "nav.applications",
    title: "Apps",
    url: "/applications",
    section: "Platform",
    permission: "applications",
    tags: ["apps"],
  },
  "nav.pppc": {
    id: "nav.pppc",
    title: "Privacy Permissions",
    url: "/configurations/pppc",
    section: "Platform",
    permission: "configurations",
    tags: ["security", "compliance"],
  },
  "nav.appConfig": {
    id: "nav.appConfig",
    title: "App Configuration",
    url: "/configurations/app-config",
    section: "Platform",
    permission: "configurations",
    tags: ["apps"],
  },
  "nav.configurations": {
    id: "nav.configurations",
    title: "Configurations",
    url: "/configurations",
    section: "Platform",
    permission: "configurations",
    tags: ["fleet"],
  },
  "nav.patching": {
    id: "nav.patching",
    title: "Patching",
    url: "/patching",
    section: "Platform",
    permission: "patching",
    tags: ["security"],
  },
  "nav.vulnerabilities": {
    id: "nav.vulnerabilities",
    title: "Vulnerabilities",
    url: "/vulnerabilities",
    section: "Platform",
    permission: "vulnerabilities",
    tags: ["security", "compliance"],
  },
  "nav.compliance": {
    id: "nav.compliance",
    title: "Compliance",
    url: "/compliance",
    section: "Platform",
    permission: "compliance",
    tags: ["compliance"],
  },
  "nav.tags": {
    id: "nav.tags",
    title: "Tags",
    url: "/tags",
    section: "Platform",
    permission: "tags",
    tags: ["fleet"],
  },
  "nav.usersAndRoles": {
    id: "nav.usersAndRoles",
    title: "Users & Roles",
    url: "/admin/users-and-roles",
    section: "System Administration",
    permission: "users",
    tags: ["admin"],
  },
  "nav.enrollment": {
    id: "nav.enrollment",
    title: "Enrollment Profiles",
    url: "/admin/enrollment-profiles",
    section: "System Administration",
    permission: "enrollment",
    tags: ["admin", "fleet"],
  },
  "nav.scripts": {
    id: "nav.scripts",
    title: "Scripts",
    url: "/scripts",
    section: "System Administration",
    permission: "scripts",
    tags: ["admin"],
  },
  "nav.logs": {
    id: "nav.logs",
    title: "Logs",
    url: "/logs",
    section: "System Administration",
    permission: "logs",
    tags: ["compliance", "admin"],
  },
  "nav.settings": {
    id: "nav.settings",
    title: "Settings",
    url: "/settings",
    section: "System Administration",
    permission: "settings",
    tags: ["admin"],
  },
  "nav.tenants": {
    id: "nav.tenants",
    title: "Tenants",
    url: "/admin/tenants",
    section: "Tenant Administration",
    permission: "tenants",
    tags: ["admin"],
  },
} as const satisfies Record<string, NavItemEntry>;

export type NavItemId = keyof typeof NAV_ITEM_REGISTRY;
export const NAV_ITEM_IDS = Object.keys(NAV_ITEM_REGISTRY) as NavItemId[];
export const isNavItemId = (v: string): v is NavItemId =>
  Object.prototype.hasOwnProperty.call(NAV_ITEM_REGISTRY, v);

// ---------- Dashboard widgets ----------

export interface WidgetEntry {
  id: string;
  title: string;
  description: string;
  tags: ReadonlyArray<string>;
}

/**
 * Widgets that an admin may pin to the role-scoped dashboard. These are
 * lightweight cards that the dashboard already renders or can render — see
 * `pages/Dashboard.tsx` for how `WidgetConfig[]` drives rendering order.
 */
export const DASHBOARD_WIDGET_REGISTRY = {
  "widget.fleetSummary": {
    id: "widget.fleetSummary",
    title: "Fleet summary",
    description: "Active vs inactive devices by platform.",
    tags: ["fleet", "overview"],
  },
  "widget.complianceScore": {
    id: "widget.complianceScore",
    title: "Compliance score",
    description: "Aggregate compliance grade across the fleet.",
    tags: ["compliance"],
  },
  "widget.nonCompliantDevices": {
    id: "widget.nonCompliantDevices",
    title: "Non-compliant devices",
    description: "List of devices currently failing policy.",
    tags: ["compliance"],
  },
  "widget.openVulnerabilities": {
    id: "widget.openVulnerabilities",
    title: "Open vulnerabilities",
    description: "High/critical CVEs affecting your fleet.",
    tags: ["security", "compliance"],
  },
  "widget.patchStatus": {
    id: "widget.patchStatus",
    title: "Patch status",
    description: "Devices behind on OS or app patches.",
    tags: ["security"],
  },
  "widget.recentActivity": {
    id: "widget.recentActivity",
    title: "Recent activity",
    description: "Latest enrollment, lock, and configuration events.",
    tags: ["overview", "admin"],
  },
  "widget.appAdoption": {
    id: "widget.appAdoption",
    title: "App adoption",
    description: "Top deployed applications by install count.",
    tags: ["apps"],
  },
  "widget.userActivity": {
    id: "widget.userActivity",
    title: "User activity",
    description: "Sign-ins and admin actions over time.",
    tags: ["admin", "overview"],
  },
} as const satisfies Record<string, WidgetEntry>;

export type WidgetId = keyof typeof DASHBOARD_WIDGET_REGISTRY;
export const WIDGET_IDS = Object.keys(DASHBOARD_WIDGET_REGISTRY) as WidgetId[];
export const isWidgetId = (v: string): v is WidgetId =>
  Object.prototype.hasOwnProperty.call(DASHBOARD_WIDGET_REGISTRY, v);

/** Routes the workspace's `landing_path` may point to. Anything else is rejected. */
export const ALLOWED_LANDING_PATHS: ReadonlyArray<string> = NAV_ITEM_IDS.map(
  (id) => NAV_ITEM_REGISTRY[id].url,
);

/** Routes for which the workspace may stash default query params. */
export const PAGE_DEFAULT_PATHS: ReadonlyArray<string> = [
  "/devices",
  "/applications",
  "/vulnerabilities",
  "/compliance",
  "/admin/users-and-roles",
];
